import type { ReactNode } from 'react';

/**
 * UI surface markers — `data-ui="UI-<CODE>-<nnn>"` on the root element of every
 * screen / tab / panel / dialog listed in `dev/ecosystem/ui/<app>.md`.
 * Why: tickets, screenshots and Playwright steps cite the id; `scripts/check-ui-surfaces.ts`
 * diffs the inventory against these literals so the map cannot drift silently.
 */
export type UiSurfaceId = `UI-${string}-${string}`;

/** Spread onto any element or MUI component: `<Card {...uiSurface('UI-PTL-009')}>`. */
export const uiSurface = (id: UiSurfaceId) => ({ 'data-ui': id }) as const;

/** For MUI portal components that need the marker on the Paper: `slotProps={{ paper: uiPaper(id) }}`. */
export const uiPaper = (id: UiSurfaceId) => ({ 'data-ui': id }) as Record<string, string>;

/**
 * Wrapper for surfaces whose component renders no DOM of its own (route elements behind
 * `ProtectedRoute`, fragments). `display: contents` keeps layout untouched.
 */
export function UiSurface({ id, children }: { id: UiSurfaceId; children: ReactNode }) {
  return (
    <div data-ui={id} style={{ display: 'contents' }}>
      {children}
    </div>
  );
}
